/**
 * Paint the colour the author picked, and pick ink that survives it (2026-08-18).
 *
 * ## What changed, and why the previous design was wrong
 *
 * The first version of this module DERIVED both surfaces from the author's hue:
 * light mode got `L=94% S≤45%`, dark mode `L=22% S≤30%`. The reasoning was sound
 * on its own terms — a single stored colour cannot be read-correct in both themes,
 * so re-target it per theme and the contrast floor holds for every hue.
 *
 * It was still wrong, and the ticket owner said so plainly: *"element backrgounds
 * colours, cant limit to a certain palette. that should be free."* Re-targeting
 * lightness and capping saturation IS a palette — a nine-million-colour input with
 * a two-band output. Pick `#66ff00` and you got a pale mint wash in light mode and
 * a muted olive in dark; the vivid green you chose existed nowhere on screen. A
 * colour picker that cannot produce the colour is a worse lie than no picker.
 *
 * So the colour is now used AS PICKED, alpha included, and the derivation is
 * confined to the one thing the author did not choose and should not have to: the
 * TEXT colour. That split is the whole design.
 *
 * | property | who decides | why |
 * |---|---|---|
 * | background | the author, exactly | it is their document; a vivid card is a legitimate choice |
 * | alpha | the author, exactly | "colours can be full opacity or 0 opacity, its a user desition" |
 * | border | derived from the composited fill | an edge, so the card does not bleed into the page |
 * | ink | derived from the composited fill | the contrast guarantee, which is ours to keep |
 *
 * ## Compositing, and why it is not optional
 *
 * A tint with alpha is not a colour until it is over something. `#66ff0080` sits on
 * white paper in light mode and on `#2c2e33` in dark, and those composite to two
 * very different colours — one of which wants black text and the other white. So
 * the ink decision runs the alpha blend for EACH theme against that theme's page
 * surface, and reads the luminance of the result. This is why `inkLight` and
 * `inkDark` can still differ even though `bgLight === bgDark`: the fill is one
 * value, what it lands on is not.
 *
 * At `alpha: 0` the composite IS the page surface, so a fully transparent tint
 * degrades exactly to an untinted card — correct ink, subtle edge, no special case.
 */

type Rgba = { r: number; g: number; b: number; a: number };

/**
 * `#rrggbb` or `#rrggbbaa` -> channels in 0-255 plus alpha in 0-1.
 * Assumes the caller validated the format with `isCustomTint`.
 */
const hexToRgba = ( hex: string ): Rgba => ({
  r: parseInt( hex.slice( 1, 3 ), 16 ),
  g: parseInt( hex.slice( 3, 5 ), 16 ),
  b: parseInt( hex.slice( 5, 7 ), 16 ),
  a: hex.length >= 9 ? parseInt( hex.slice( 7, 9 ), 16 ) / 255 : 1
});

/** Source-over alpha compositing: what the eye actually receives. */
const compositeOver = ( fg: Rgba, bg: { r: number; g: number; b: number }) => ({
  r: fg.r * fg.a + bg.r * ( 1 - fg.a ),
  g: fg.g * fg.a + bg.g * ( 1 - fg.a ),
  b: fg.b * fg.a + bg.b * ( 1 - fg.a )
});

/**
 * The page surface each theme puts behind a card. These are the values the app
 * actually paints (`--mantine-color-white` and `dark-6`), so the composite here
 * matches the composite the browser performs.
 */
const PAGE_LIGHT = { r: 255, g: 255, b: 255 };
const PAGE_DARK = { r: 0x2c, g: 0x2e, b: 0x33 };

/**
 * Relative luminance, WCAG 2.x. It has to be the perceptual measure rather than a
 * naive average — `#ffff00` and `#0000ff` have nearly identical naive brightness
 * and could not be more different to read against.
 */
const relativeLuminance = ({ r, g, b }: { r: number; g: number; b: number }): number => {
  const channel = ( v: number ): number => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : (( c + 0.055 ) / 1.055 ) ** 2.4;
  };
  return 0.2126 * channel( r ) + 0.7152 * channel( g ) + 0.0722 * channel( b );
};

/** The app's text colours, so a tinted card's ink matches untinted ink beside it. */
const INK_DARK = '#212529'; // Mantine gray-9 / the app's primary text
const INK_LIGHT = '#f8f9fa'; // Mantine gray-0

/** WCAG 2.x contrast ratio. Order-independent; both arguments are luminances. */
const contrastRatio = ( a: number, b: number ): number =>
  ( Math.max( a, b ) + 0.05 ) / ( Math.min( a, b ) + 0.05 );

const LUMINANCE_INK_DARK = relativeLuminance({ r: 0x21, g: 0x25, b: 0x29 });
const LUMINANCE_INK_LIGHT = relativeLuminance({ r: 0xf8, g: 0xf9, b: 0xfa });

/**
 * The ink that is ACTUALLY more readable on this surface — measured, not guessed.
 *
 * ⚠️ This was `luminance > 0.5 ? dark : light`, and 0.5 is wrong. It is the usual
 * shorthand for "is this colour light or dark", but that is a different question
 * from "which of these two inks reads better on it", and the two answers diverge
 * over a wide, very reachable band.
 *
 * The real crossover — where black and white contrast EQUALLY against a surface —
 * is at a luminance of about **0.21**, not 0.5, because the two candidates are not
 * symmetric about the middle: contrast is a ratio, so the near-white ink runs out
 * of headroom long before the near-black one does. Every surface between 0.21 and
 * 0.5 was therefore given WHITE text when BLACK was the more readable choice.
 *
 * That band is not a corner case, and the old design hid it: surfaces were pinned
 * to L=94% or L=22%, both far from either threshold, so the constant never had to
 * be right. Once the author's own colour became the surface it did — and the first
 * mid-tone tested (a violet at half alpha over white, composite luminance ≈ 0.35)
 * came back with white text at a 2.6:1 contrast ratio where black would have given
 * 5.9:1. Below the WCAG floor, on a SHARED report, silently.
 *
 * Comparing the two ratios directly removes the constant altogether, which is the
 * point: there is no threshold left to get wrong, and the function now states its
 * own intent — pick the ink that contrasts more.
 */
const inkFor = ( surface: { r: number; g: number; b: number }): string => {
  const luminance = relativeLuminance( surface );
  return contrastRatio( luminance, LUMINANCE_INK_DARK ) >= contrastRatio( luminance, LUMINANCE_INK_LIGHT )
    ? INK_DARK
    : INK_LIGHT;
};

const clamp255 = ( v: number ): number => Math.round( Math.min( 255, Math.max( 0, v )));

/**
 * The card's edge: the composited fill stepped AWAY from the page.
 *
 * Not the picked colour, and not a fixed grey. A fixed grey would fight every
 * vivid tint; the picked colour at the same alpha would be invisible against its
 * own fill at `alpha: 1`. Stepping the composite keeps one rule that works from
 * fully transparent to fully opaque, in both themes.
 */
const BORDER_STEP = 26; // ~10% of the channel range: visible, never a second frame.

const borderFor = ( surface: { r: number; g: number; b: number }): string => {
  const direction = relativeLuminance( surface ) > 0.5 ? -1 : 1;
  const step = BORDER_STEP * direction;
  return `rgb(${ clamp255( surface.r + step ) } ${ clamp255( surface.g + step ) } ${
    clamp255( surface.b + step ) })`;
};

export type TintSurfaces = {
  bgLight: string;
  bgDark: string;
  borderLight: string;
  borderDark: string;
  inkLight: string;
  inkDark: string;
};

/**
 * The six CSS values a custom tint needs, from the one colour the author picked.
 *
 * Returned as a flat record of plain strings because they become CSS custom
 * properties on the card, and `light-dark()` in the stylesheet picks the pair —
 * the derivation cannot happen in CSS, but the theme SWITCH must, or a card would
 * need re-rendering to follow a theme change.
 *
 * ⚠️ `bgLight` and `bgDark` are INTENTIONALLY the same string. The author picked
 * one colour and meant it; emitting two would be the re-targeting this module was
 * rewritten to remove. The pair still exists in the shape because the border and
 * the ink genuinely do differ per theme, and the card consumes all six the same way.
 */
export const deriveTintPair = ( hex: string ): TintSurfaces => {
  const picked = hexToRgba( hex );

  // Emitted with the alpha intact so the browser composites it over whatever is
  // actually behind the card — including a card sitting on a tinted section.
  const fill = `rgba(${ picked.r }, ${ picked.g }, ${ picked.b }, ${
    Number( picked.a.toFixed( 3 )) })`;

  const overLight = compositeOver( picked, PAGE_LIGHT );
  const overDark = compositeOver( picked, PAGE_DARK );

  return {
    bgLight: fill,
    bgDark: fill,
    borderLight: borderFor( overLight ),
    borderDark: borderFor( overDark ),
    inkLight: inkFor( overLight ),
    inkDark: inkFor( overDark )
  };
};
